import { useTranslation } from "react-i18next";

import { Box, ButtonBase } from "@mui/material";
import { alpha } from "@mui/material/styles";

import TokenSelectGroup from "@plannel/components/ui/TokenSelectGroup";
import { MODAL_SECTION_LABEL_SX } from "@plannel/components/modal/BaseConfigModal";
import { FS } from "@plannel/utils/font-util";

/*
 * 그룹으로 묶인 다중선택 토큰 — 설정 모달 본문에서 **부모-자식 트리를 대신한다**.
 *
 *   <GroupedTokenSelect
 *     groups={[{ key, label, options: [{ value, label }] }]}   // label 은 이미 번역된 문자열
 *     value={selectedValues}                                   // 선택된 value 배열
 *     onChange={next => …}                                     // groups 순서로 정렬돼 온다
 *   />
 *
 * **언제 트리 대신 이걸 쓰나 — 판단 기준은 모양이 아니라 「무엇이 저장되는가」다.**
 * 부모 노드가 저장되지 않고 "그 밑을 한 번에 켜는 편의 장치"일 뿐이라면 트리일 이유가 없다.
 * 부모를 그룹 제목으로 내리면 정보를 하나도 잃지 않으면서, 부모↔자식 체크 상태를 손으로 맞추던
 * 코드가 통째로 사라진다(로케이션 선택이 실제로 그 로직만 200줄 가까이 됐다).
 * 반대로 부모 자체가 값이면 그건 진짜 트리다 — 그대로 둔다.
 *
 * 적용처: DP Workbench User Customization 의 계층 필터(HrchyMultiSelect)·로케이션 필터(MultiSelectLocation).
 *
 * props
 *   groups     : [{ key, label, options }] — `label` 이 빈 문자열이면 그 그룹은 제목 없이 토큰만 (예: 사이트 없는 로케이션)
 *   value      : 선택된 value 배열
 *   onChange   : (nextValues) => void. **groups 에 나온 순서로 정렬**해서 준다 —
 *                같은 조합이면 늘 같은 배열이 나와야 저장값이 흔들리지 않는다
 *   maxHeight  : 그룹 목록 스크롤 한계(기본 320)
 *
 * ⚠ 전체선택 링크는 **헤더에 하나만** 둔다(각 그룹은 `showSelectAll={false}`).
 *   그룹이 5개면 링크도 5개가 되어 무엇을 누르는지 흐려진다.
 */
const GroupedTokenSelect = ({ groups = [], value = [], onChange, maxHeight = 320 }) => {
  const { t } = useTranslation();

  const allValues = groups.flatMap(group => group.options.map(option => option.value));
  const selected = value.filter(v => allValues.includes(v));
  const allOn = allValues.length > 0 && allValues.every(v => selected.includes(v));

  const apply = next => onChange(allValues.filter(v => next.includes(v)));

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, pb: 1.5 }}>
        <Box sx={MODAL_SECTION_LABEL_SX}>{`${t("selected")} ${selected.length}`}</Box>
        <ButtonBase
          disabled={allValues.length === 0}
          onClick={() => apply(allOn ? [] : allValues)}
          sx={{
            fontSize: FS.CAPTION, fontWeight: 500, lineHeight: 1,
            px: 0.75, py: 0.5, borderRadius: 1, color: "primary.main",
            "&:hover": { bgcolor: theme => alpha(theme.palette.primary.main, 0.08) },
            "&.Mui-disabled": { color: "text.disabled" },
          }}>
          {allOn ? t("clearAll") : t("selectAll")}
        </ButtonBase>
      </Box>
      <Box sx={{ maxHeight, overflowY: "auto", overflowX: "hidden" }}>
        {groups.map(group => (
          <TokenSelectGroup
            key={group.key}
            label={group.label}
            options={group.options}
            value={selected}
            showSelectAll={false}
            onChange={next => {
              // next 는 이 그룹 몫만 담고 온다 — 다른 그룹 선택은 그대로 살려서 합친다
              const others = selected.filter(v => !group.options.some(option => option.value === v));
              apply([...others, ...next]);
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default GroupedTokenSelect;
